'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface ConferenceProduct {
  id: string;
  name: string;
  description: string;
  emoji: string;
  color: string;
  badge?: string;
  image?: string;
}

const conferenceProducts: ConferenceProduct[] = [
  { id: 'kraft-folders', name: 'Kraft Paper Folders', description: 'Elegant conference document folders', emoji: '📁', color: 'bg-amber-50', badge: 'No Plastic', image: '/assets/images/folders-1787042123432.jpeg' },
  { id: 'cotton-lanyards', name: 'Cotton Lanyards', description: 'Soft cotton ID card lanyards', emoji: '🏷️', color: 'bg-green-50', image: '/assets/images/cotton_lanyard-1787042123398.jpeg' },
  { id: 'kraft-id-cards', name: 'Kraft Paper ID Cards', description: 'Recycled kraft paper ID holders', emoji: '💼', color: 'bg-orange-50', image: '/assets/images/id_card_kraft-1787042635382.jpeg' },
  { id: 'seed-id-cards', name: 'Seed Paper ID Cards', description: 'Plant-after-use seed paper IDs', emoji: '🌱', color: 'bg-lime-50', badge: 'Plantable', image: '/assets/images/seed_paper_id-1787042671153.jpeg' },
  { id: 'seed-notepad', name: 'Seed Paper Notepad', description: 'Conference notepads you can plant', emoji: '📓', color: 'bg-teal-50', badge: 'Eco', image: '/assets/images/notepads-1787043144020.jpeg' },
  { id: 'seed-pens', name: 'Seed Pens', description: 'Write, use, then plant the pen', emoji: '✏️', color: 'bg-emerald-50', image: '/assets/images/seed_pens-1787043144018.jpeg' },
  { id: 'kraft-certificates', name: 'Kraft Paper Certificates', description: 'Premium kraft paper certificates', emoji: '🏅', color: 'bg-yellow-50', image: '/assets/images/certificate-1787043194669.jpeg' },
  { id: 'event-kits', name: 'Complete Event Kits', description: 'All-in-one no-plastic event bundles', emoji: '🌟', color: 'bg-green-50', badge: 'Bundle' },
];

const features = [
  { emoji: '♻️', title: '100% Plastic-Free', desc: 'Every item in our conference kits is completely free from plastic' },
  { emoji: '🌱', title: 'Plantable Products', desc: 'Seed paper items can be planted after use — zero waste' },
  { emoji: '🌿', title: 'ESG Benefits', desc: 'Supports all three pillars of ESG' },
  { emoji: '🎨', title: 'Custom Branding', desc: 'Your logo and brand on every item in the kit' },
];

export default function ConferenceKitsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 lg:py-20 bg-primary-dark text-white" id="conference-kits">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block bg-white/15 text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
              No Plastic Zone
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Conference & Event Kits
            </h2>
            <p className="text-white/70 text-base font-normal max-w-2xl mx-auto">
              Make your next event truly sustainable. Our no-plastic conference kits combine functionality with environmental responsibility.
            </p>
          </div>

          {/* Features row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {features.map((feat) => (
              <div
                key={feat.title}
                className="bg-white/8 rounded-xl p-4 border border-white/10 hover:border-white/30 transition-colors duration-200"
              >
                <span className="text-2xl mb-2 block">{feat.emoji}</span>
                <h3 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {feat.title}
                </h3>
                <p className="text-xs text-white/60 font-normal leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>

          {/* Products grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-10">
            {conferenceProducts.map((product, i) => (
              <Link
                key={product.id}
                href={`/products/${product.id}`}
                className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-white/30 hover:bg-white/10 transition-all duration-300 cursor-pointer"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className={`${product.color} aspect-square flex items-center justify-center relative`}>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl group-hover:scale-110 transition-transform duration-300">{product.emoji}</span>
                  )}
                  {product.badge && (
                    <span className="absolute top-2 left-2 bg-primary text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {product.name}
                  </h3>
                  <p className="text-xs text-white/50 mt-1 font-normal">{product.description}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-light hover-arrow">
                    View details
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <a href="#contact" className="inline-flex items-center gap-2 bg-white text-primary-dark font-semibold px-8 py-3 rounded-btn hover:bg-gray-50 transition-all duration-200 hover:scale-105 active:scale-95 text-base">
              Build Your Conference Kit
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
